import { useEffect, useState } from "react";
import { MessageSquare, Check, Inbox, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { EXAM_LABELS, SUBJECT_LABELS } from "@/lib/pyqMeta";
import { MathText } from "@/components/MathText";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

const CAT_LABELS = {
  WRONG_ANSWER: "Wrong answer",
  QUESTION_ERROR: "Error in question",
  SOLUTION_UNCLEAR: "Solution unclear",
  DIAGRAM_ISSUE: "Diagram issue",
  OTHER: "Other",
};
const TABS = [
  { key: "NEW", label: "New" },
  { key: "RESOLVED", label: "Resolved" },
  { key: "ALL", label: "All" },
];

export const AdminFeedback = ({ focusQuestionId = null }) => {
  const [status, setStatus] = useState("NEW");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = () => {
    setLoading(true);
    const params = {};
    if (focusQuestionId) params.questionId = focusQuestionId;
    else if (status !== "ALL") params.status = status;
    api.get("/admin/feedback", { params })
      .then((r) => setItems(r.data.items))
      .finally(() => setLoading(false));
  };
  useEffect(load, [status, focusQuestionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const resolve = async (id) => {
    setBusy(id);
    try {
      await api.post(`/admin/feedback/${id}/resolve`);
      toast.success("Marked resolved");
      setItems((prev) => (status === "NEW" ? prev.filter((f) => f.id !== id) : prev.map((f) => (f.id === id ? { ...f, status: "RESOLVED" } : f))));
    } catch {
      toast.error("Could not update");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-center gap-2">
        {focusQuestionId ? (
          <span className="rounded-full border border-primary bg-primary/10 px-4 py-1.5 text-sm font-medium text-primary" data-testid="feedback-focus-banner">
            Showing feedback for one question
          </span>
        ) : (
          TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`feedback-tab-${t.key}`}
              onClick={() => setStatus(t.key)}
              className={cn(
                "rounded-full border px-4 py-1.5 text-sm font-medium transition-colors",
                status === t.key ? "border-primary bg-primary text-primary-foreground" : "border-border bg-secondary text-muted-foreground"
              )}
            >
              {t.label}
            </button>
          ))
        )}
        <Button variant="ghost" size="sm" onClick={load} className="ml-auto gap-1.5"><RefreshCw className="h-4 w-4" /> Refresh</Button>
      </div>

      {loading ? (
        <div className="space-y-3">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-28 w-full rounded-2xl" />)}</div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card p-14 text-center" data-testid="feedback-empty">
          <Inbox className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
          <p className="font-heading text-lg font-semibold">No feedback here</p>
          <p className="mt-1 text-sm text-muted-foreground">Student feedback on questions and solutions will appear here.</p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="feedback-list">
          {items.map((f) => (
            <div key={f.id} data-testid={`feedback-card-${f.id}`} className="rounded-2xl border border-border bg-card p-5">
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <Badge className="rounded-full"><MessageSquare className="mr-1 h-3 w-3" /> {CAT_LABELS[f.category] || f.category}</Badge>
                {f.question && (
                  <span className="text-xs font-semibold uppercase text-muted-foreground">
                    {EXAM_LABELS[f.question.examType] || f.question.examType} · {SUBJECT_LABELS[f.question.subject] || f.question.subject} · {f.question.chapter}
                  </span>
                )}
                <span className="text-xs text-muted-foreground">by {f.studentName || "student"} · {new Date(f.createdAt).toLocaleDateString()}</span>
                {f.status === "RESOLVED" && <Badge variant="outline" className="rounded-full text-[10px] text-green-600">Resolved</Badge>}
                {f.status === "NEW" && (
                  <Button size="sm" variant="outline" onClick={() => resolve(f.id)} disabled={busy === f.id} data-testid={`resolve-feedback-${f.id}`} className="ml-auto gap-1.5 rounded-full">
                    <Check className="h-4 w-4" /> Mark resolved
                  </Button>
                )}
              </div>
              {f.question?.questionText && (
                <MathText block className="line-clamp-2 text-sm text-foreground/80" text={f.question.questionText} />
              )}
              {f.comment && <p className="mt-2 rounded-lg bg-secondary/50 p-3 text-sm">{f.comment}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
